/**
 * Hybrid local/server tick state for reconciliation row-verification.
 *
 *   - localStorage = primary (instant, offline-safe)
 *   - server = source-of-truth-on-conflict (multi-device, multi-admin)
 *
 * Sync model:
 *   - Page mount: read local cache → render → fetch server in background → MERGE
 *     (server wins; if server has tick X, mark X locally too; if local has tick Y
 *     not yet on server, queue an upsert to push Y up)
 *   - User toggles: optimistic local update + UI redraw + queue background POST
 *   - When offline: queue persists in localStorage (`recon_tick_queue_<gid>_<key>`),
 *     drains on next online sync attempt
 *
 *  This module is React-agnostic; consumers manage their own state via subscribe().
 */

import api from './api';

// =================== localStorage layer ===================

const TICKS_KEY = (gid, periodKey) => `recon_ticks_${gid}_${periodKey}`;
const QUEUE_KEY = (gid, periodKey) => `recon_tick_queue_${gid}_${periodKey}`;

function safeLoad(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeSave(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (e) {
    console.warn('localStorage write failed', e);
  }
}

// =================== Public API ===================

export function readLocalTicks(groupId, periodKey) {
  return new Set(safeLoad(TICKS_KEY(groupId, periodKey), []));
}

function writeLocalTicks(groupId, periodKey, set) {
  safeSave(TICKS_KEY(groupId, periodKey), Array.from(set));
}

function readQueue(groupId, periodKey) {
  // queue shape: [{ rowId, action, queued_at }, ...]
  return safeLoad(QUEUE_KEY(groupId, periodKey), []);
}

function writeQueue(groupId, periodKey, queue) {
  safeSave(QUEUE_KEY(groupId, periodKey), queue);
}

function enqueue(groupId, periodKey, rowIds, action) {
  const q = readQueue(groupId, periodKey);
  const now = Date.now();
  rowIds.forEach(rid => {
    // collapse: remove any prior queued op on same row
    const existing = q.findIndex(e => e.rowId === rid);
    if (existing !== -1) q.splice(existing, 1);
    q.push({ rowId: rid, action, queued_at: now });
  });
  writeQueue(groupId, periodKey, q);
}

/**
 * Toggle ticks locally (immediate) and enqueue server sync.
 * Returns the new set so the caller can re-render.
 */
export function toggleTicks(groupId, periodKey, rowIds, action) {
  const set = readLocalTicks(groupId, periodKey);
  rowIds.forEach(rid => {
    if (action === 'tick') set.add(rid);
    else set.delete(rid);
  });
  writeLocalTicks(groupId, periodKey, set);
  enqueue(groupId, periodKey, rowIds, action);
  // fire-and-forget server sync
  drainQueue(groupId, periodKey).catch(() => { /* swallow — will retry */ });
  return set;
}

/**
 * Drain the queue against the server. Best-effort, silent failure.
 */
export async function drainQueue(groupId, periodKey) {
  if (!navigator.onLine) return;
  const q = readQueue(groupId, periodKey);
  if (q.length === 0) return;
  // Group by action and POST in 2 calls max
  const tickIds = q.filter(e => e.action === 'tick').map(e => e.rowId);
  const untickIds = q.filter(e => e.action === 'untick').map(e => e.rowId);
  try {
    if (tickIds.length) {
      await api.post(`/groups/${groupId}/reconcile/ticks`, {
        period_key: periodKey,
        row_ids: tickIds,
        action: 'tick',
      });
    }
    if (untickIds.length) {
      await api.post(`/groups/${groupId}/reconcile/ticks`, {
        period_key: periodKey,
        row_ids: untickIds,
        action: 'untick',
      });
    }
    writeQueue(groupId, periodKey, []);  // success — clear queue
  } catch (e) {
    // network / 5xx — leave queue intact, retry on next call
    console.warn('Tick sync failed (will retry):', e?.response?.status || e?.message);
  }
}

/**
 * Hydrate from server, merge with local. Server-wins on conflict.
 *
 * Strategy:
 *   1. Pull server tick set
 *   2. Merge into localStorage:
 *      - Any tick on server but not local → add locally
 *      - Any tick local but not server → keep locally + ensure it's in queue
 *        (means: user ticked offline, server doesn't know yet; preserve and push)
 *   3. Drain queue
 */
export async function hydrateAndSync(groupId, periodKey) {
  let serverSet = new Set();
  try {
    const r = await api.get(`/groups/${groupId}/reconcile/ticks`, { params: { period_key: periodKey } });
    serverSet = new Set((r.data.ticks || []).map(t => t.row_id));
  } catch (e) {
    // offline / 5xx — fall back to local only
    return readLocalTicks(groupId, periodKey);
  }
  const localSet = readLocalTicks(groupId, periodKey);
  const queue = readQueue(groupId, periodKey);
  const queuedTicks = new Set(queue.filter(e => e.action === 'tick').map(e => e.rowId));
  const queuedUnticks = new Set(queue.filter(e => e.action === 'untick').map(e => e.rowId));

  const merged = new Set(serverSet);
  // Preserve local-only ticks that haven't been pushed yet (queued_tick)
  queuedTicks.forEach(rid => merged.add(rid));
  // Honour queued unticks even if server still has them
  queuedUnticks.forEach(rid => merged.delete(rid));
  // Local ticks that are NOT in server AND NOT in queue → orphans (local cache stale).
  // These get DROPPED — server is authoritative for ticks not in our pending queue.
  // (handled by `merged` starting from `serverSet`)

  writeLocalTicks(groupId, periodKey, merged);
  await drainQueue(groupId, periodKey);
  return merged;
}

/**
 * Build a stable period_key from the current period query params.
 * Same query → same key, deterministic across admins/devices.
 */
export function derivePeriodKey({ preset, from, to }) {
  if (preset === 'all') return 'all';
  if (preset === 'this_month' || preset === 'last_month') {
    // Use the from-date's YYYY-MM if available, else current month.
    const d = from ? new Date(from) : new Date();
    return `month:${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  }
  // sliding windows (last_30, last_90) and custom — use the from..to literal
  return `range:${from || 'open'}_${to || 'open'}`;
}

// Auto-drain when the tab regains network — best-effort
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // Drain ALL queued period-keys for this user
    Object.keys(localStorage)
      .filter(k => k.startsWith('recon_tick_queue_'))
      .forEach(k => {
        const m = k.match(/^recon_tick_queue_([^_]+)_(.+)$/);
        if (m) drainQueue(m[1], m[2]).catch(() => {});
      });
  });
}
